import React from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import {
    shouldShowBulkUploads,
    shouldShowCreateRule,
    shouldShowNetworkRules,
    shouldShowScheduledDeployment
} from 'Selectors';
import { Button } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { RoleService } from 'Services';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import RulesHeader from 'Components/RulesHeader';
import AddNewRuleButton from 'Components/RulesHeader/AddNewRuleButton';
import DeployButtonSelector from 'Components/DeployButtonSelector';
import ButtonGroup from 'Components/ButtonGroup/ButtonGroup';
import styles from './Header.css';

type Props = {
    disableLink?: boolean;
};

const Header = ({ disableLink }: Props) => {
    const showCreateRuleButton = RoleService.hasCreateRuleAccess && shouldShowCreateRule();
    const showDeploymentButton = RoleService.hasDeployAccess && !shouldShowScheduledDeployment();
    const showScheduledDeploymentButton = RoleService.hasDeployAccess && shouldShowScheduledDeployment();
    const showBulkUploadsButton = RoleService.hasEditRuleOrBulkUploadAccess && shouldShowBulkUploads();
    const landlordInfo = useGetLandlordInfo(showCreateRuleButton);
    const { path } = useRouteMatch();
    const { t } = useTranslation();
    const history = useHistory();

    const redirectToBulkUpload = () => {
        history.push(`${path}/bulk-upload`);
    };

    return (
        <RulesHeader>
            <RulesHeader.Left isSandbox title={t('Global Rules')} disableLink={disableLink} />
            <RulesHeader.Right>
                <ButtonGroup direction='row'>
                    {showBulkUploadsButton && (
                        <div className={styles.bulkUpload}>
                            <Button
                                dataTestId='upload-global-rules-btn'
                                variant='tertiary'
                                iconName='upload'
                                onClick={handleOnClickEvent(redirectToBulkUpload)}
                                onKeyDown={handleKeyEvent(redirectToBulkUpload)}
                            >
                                {t('Upload Rule Settings')}
                            </Button>
                        </div>
                    )}

                    {(showCreateRuleButton || shouldShowNetworkRules()) && (
                        <AddNewRuleButton isTenantWithLandlord={landlordInfo.data?.tenantWithLandlord} />
                    )}
                </ButtonGroup>

                {showCreateRuleButton && (showScheduledDeploymentButton || showDeploymentButton) && (
                    <hr className={styles.separator} />
                )}

                <DeployButtonSelector
                    rulesType='globalRules'
                    hasDeployAccess={RoleService.hasDeployAccess}
                    shouldShowScheduledDeployment={shouldShowScheduledDeployment()}
                />
            </RulesHeader.Right>
        </RulesHeader>
    );
};

export default Header;
